import React, { useCallback, useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Gavel, FileCheck2, ExternalLink, Loader2, ShieldCheck, ShieldAlert } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// Official jurisdiction-based Return of Service / Non-Service — server review + e-signature.
export function ReturnOfService({ orderId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [certify, setCertify] = useState(false);
  const [signature, setSignature] = useState("");
  const [edits, setEdits] = useState({});
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/orders/${orderId}/return-of-service`);
      setData(data);
    } catch { /* not a party / not available */ }
    finally { setLoading(false); }
  }, [orderId]);
  useEffect(() => { load(); }, [load]);

  if (loading || !data) return null;
  const served = data.served;
  const signed = data.signed;
  const val = (k) => (edits[k] !== undefined ? edits[k] : (data[k] || ""));
  const set = (k) => (e) => setEdits((s) => ({ ...s, [k]: e.target.value }));

  const sign = async () => {
    if (!certify) return toast.error("Please certify the return is accurate before signing.");
    if (!signature.trim()) return toast.error("An electronic signature is required.");
    setBusy(true);
    try {
      await api.post(`/orders/${orderId}/return-of-service/sign`, {
        signature, certify: true,
        case_style: val("case_style"), comments: val("comments"),
        non_service_reason: val("non_service_reason"), location: val("location"),
        accepted_by: val("accepted_by"),
      });
      toast.success("Official return e-signed, locked, and stored in the Document Vault.");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not sign the return."); }
    finally { setBusy(false); }
  };

  const pdfUrl = `${BACKEND}/api/orders/${orderId}/return-of-service.pdf`;

  // Open the signed return from the Document Vault using the existing authenticated
  // file-view-token flow (top-level navigation; Chrome renders the PDF natively).
  const openSigned = async () => {
    const fid = data.document_id;
    if (!fid) return toast.error("Signed return not found.");
    const tab = window.open("about:blank", "_blank");
    try {
      const { data: t } = await api.post(`/files/${fid}/view-token`);
      const url = `${BACKEND}/api/files/${fid}?token=${encodeURIComponent(t.token)}&inline=1`;
      if (tab) tab.location.replace(url); else window.location.assign(url);
    } catch (err) {
      if (tab) tab.close();
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Unable to open signed return");
    }
  };

  // Pre-sign preview: fetch the draft PDF via the authenticated axios client (sends the session).
  const openPreview = async () => {
    try {
      const resp = await api.get(`/orders/${orderId}/return-of-service.pdf`, { responseType: "blob" });
      const blob = new Blob([resp.data], { type: "application/pdf" });
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = `${served ? "return_of_service" : "return_of_non_service"}_preview.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Unable to open preview");
    }
  };

  return (
    <div data-testid="return-of-service" className="rounded-xl border border-slate-200 bg-white p-5">
      <div className="mb-3 flex items-center gap-2">
        <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-slate-900 text-white"><Gavel className="h-5 w-5" /></span>
        <div>
          <h3 className="font-display text-base font-bold text-slate-900">Official {served ? "Return of Service" : "Return of Non-Service"}</h3>
          <p className="text-xs text-slate-500">Shelby County (Circuit/Chancery) Court — final legal completion document</p>
        </div>
        <span data-testid="ros-outcome" className={`ml-auto rounded-full px-3 py-1 text-xs font-bold ${served ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>{data.outcome_label}</span>
      </div>

      {signed ? (
        <div data-testid="ros-signed" className="space-y-3">
          <div className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
            <FileCheck2 className="h-5 w-5" /> Signed & locked by {data.signed_by} on {new Date(data.signed_at).toLocaleString()}.
          </div>
          <a href={pdfUrl} target="_blank" rel="noreferrer" data-testid="ros-view-signed" onClick={(e) => { e.preventDefault(); openSigned(); }} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">
            View signed return <ExternalLink className="h-4 w-4" />
          </a>
        </div>
      ) : (
        <div className="space-y-3">
          <p className="text-xs text-slate-500">Review the details populated from ARPS records, correct if needed, then certify and sign. Your certification and signature are required before submission.</p>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <Label>Case Style</Label>
              <Input data-testid="ros-case-style" value={val("case_style")} onChange={set("case_style")} />
            </div>
            <div><Label>Docket No.</Label><Input value={data.docket_no || ""} disabled className="bg-slate-50" /></div>
            <div><Label>Within (document)</Label><Input value={data.within || ""} disabled className="bg-slate-50" /></div>
            {served ? (
              <>
                <div><Label>Location of service</Label><Input data-testid="ros-location" value={val("location")} onChange={set("location")} /></div>
                <div><Label>Person accepting service</Label><Input data-testid="ros-accepted-by" value={val("accepted_by")} onChange={set("accepted_by")} /></div>
                <div className="sm:col-span-2"><Label>Comments</Label><Textarea rows={2} data-testid="ros-comments" value={val("comments")} onChange={set("comments")} /></div>
              </>
            ) : (
              <div className="sm:col-span-2"><Label>Reason not served</Label><Textarea rows={2} data-testid="ros-reason" value={val("non_service_reason")} onChange={set("non_service_reason")} /></div>
            )}
          </div>
          {data.gps_status && (
            <div className={`flex items-center gap-2 rounded-lg px-3 py-2 text-xs ${data.gps_status.includes("NOT VERIFIED") ? "bg-amber-50 text-amber-700" : "bg-emerald-50 text-emerald-700"}`}>
              {data.gps_status.includes("NOT VERIFIED") ? <ShieldAlert className="h-4 w-4" /> : <ShieldCheck className="h-4 w-4" />} {data.gps_status}
            </div>
          )}
          <a href={pdfUrl} target="_blank" rel="noreferrer" data-testid="ros-preview" onClick={(e) => { e.preventDefault(); openPreview(); }} className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-700 underline">
            Preview official form <ExternalLink className="h-3.5 w-3.5" />
          </a>
          <label className="flex items-start gap-2 text-sm text-slate-700">
            <input type="checkbox" data-testid="ros-certify" checked={certify} onChange={(e) => setCertify(e.target.checked)} className="mt-1" />
            I certify that the information above accurately reflects the service I performed.
          </label>
          <div>
            <Label>Electronic signature (type your full legal name)</Label>
            <Input data-testid="ros-signature" value={signature} onChange={(e) => setSignature(e.target.value)} placeholder="Full legal name" />
          </div>
          <Button data-testid="ros-sign-button" onClick={sign} disabled={busy} className="bg-slate-900 text-white hover:bg-slate-800">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : `Certify & sign ${served ? "Return of Service" : "Return of Non-Service"}`}
          </Button>
        </div>
      )}
    </div>
  );
}
